/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import type { HeatmapCell } from '@/api/narrative';
import { useHeatmapCellDetail } from './useHeatmapCellDetail';
import { engagementColor } from './colors';

export interface HeatmapDetailDrawerProps {
  cell: HeatmapCell | null;
  bucket: 'weekly' | 'daily';
  onClose: () => void;
}

/** Relative-time formatter: "2h ago", "3d ago", etc. */
function relativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.floor((now - then) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch {
    return iso;
  }
}

function outcomeBadgeClass(outcome: string): string {
  switch (outcome) {
    case 'TALKED':
      return 'bg-status-success-bg text-status-success';
    case 'NOT_HOME':
      return 'bg-bg-raised text-fg-secondary';
    case 'REFUSED':
      return 'bg-status-warning-bg/60 text-status-warning';
    case 'MOVED':
      return 'bg-status-info-bg/60 text-status-info';
    case 'COME_BACK':
      return 'bg-status-warning-bg/60 text-status-warning';
    case 'DO_NOT_CONTACT':
      return 'bg-status-danger-bg/60 text-status-danger';
    default:
      return 'bg-bg-raised text-fg-secondary';
  }
}

function leanBadgeClass(lean: string | null): string {
  if (!lean) return '';
  switch (lean) {
    case 'STRONG_US':
      return 'bg-status-success-bg text-status-success';
    case 'LEAN_US':
      return 'bg-status-success-bg/60 text-status-success';
    case 'UNDECIDED':
      return 'bg-bg-raised text-fg-secondary';
    case 'LEAN_THEM':
      return 'bg-partisan-d/40 text-partisan-d-fg';
    case 'STRONG_THEM':
      return 'bg-partisan-d/60 text-partisan-d-fg';
    case 'REFUSED':
      return 'bg-bg-elevated text-fg-muted';
    default:
      return 'bg-bg-raised text-fg-secondary';
  }
}

/**
 * Side-drawer that shows the knockback rows behind one engagement cell.
 *
 * UX:
 *   - Slides in from the right on desktop (w-full max-w-md), covers the
 *     screen on mobile.
 *   - Escape and backdrop click both close.
 *   - Focus moves to the close button on open, restores on close.
 *   - Rows are keyboard-reachable; the "Load more" button paginates.
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true", aria-labelledby pointing at a
 *     header id, body scroll lock while open, focus trap.
 */
export function HeatmapDetailDrawer({
  cell,
  bucket,
  onClose,
}: HeatmapDetailDrawerProps) {
  const headingId = 'heatmap-detail-heading';
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const detail = useHeatmapCellDetail({ cell, bucket, pageSize: 25 });

  // Remember where focus was when the drawer opened, restore on close.
  useEffect(() => {
    if (cell) {
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      // Move focus into the drawer.
      const id = window.setTimeout(() => closeBtnRef.current?.focus(), 60);
      // Body scroll lock.
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.clearTimeout(id);
        document.body.style.overflow = prevOverflow;
        lastFocusedRef.current?.focus?.();
      };
    }
    return undefined;
  }, [cell]);

  // Escape closes; light focus trap inside the drawer.
  useEffect(() => {
    if (!cell) return undefined;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cell, onClose]);

  if (!cell) return null;

  const { bg: cellBg, text: cellTxt } = engagementColor(cell.engagement_score);

  return (
    <div
      className="fixed inset-0 z-[8000] flex"
      data-testid="heatmap-detail-drawer"
      role="presentation"
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="relative ml-auto w-full max-w-md h-full bg-bg-base border-l border-border-base shadow-2xl flex flex-col"
      >
        {/* Header */}
        <header className="p-5 border-b border-border-base">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
                Precinct cell drill-down
              </p>
              <h2
                id={headingId}
                className="text-lg font-bold text-fg-primary mt-1"
              >
                Precinct {cell.precinct_num}
              </h2>
              <p className="text-xs text-fg-muted mt-0.5">
                {new Date(cell.period_start).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                · {bucket} bucket
              </p>
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label="Close drawer"
              className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded text-fg-muted hover:text-fg-primary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <span aria-hidden="true" className="text-2xl leading-none">
                ×
              </span>
            </button>
          </div>

          {/* Aggregate summary */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="bg-bg-elevated/70 rounded p-2 text-center">
              <p className="text-[10px] uppercase text-fg-muted">Knocks</p>
              <p className="text-lg font-bold text-fg-primary tabular-nums">
                {cell.knocks_attempted}
              </p>
            </div>
            <div className="bg-bg-elevated/70 rounded p-2 text-center">
              <p className="text-[10px] uppercase text-fg-muted">Conv.</p>
              <p className="text-lg font-bold text-fg-primary tabular-nums">
                {cell.conversations}
              </p>
            </div>
            <div className={`rounded p-2 text-center ${cellBg} ${cellTxt}`}>
              <p className="text-[10px] uppercase opacity-80">Score</p>
              <p className="text-lg font-bold tabular-nums">
                {(cell.engagement_score * 100).toFixed(0)}%
              </p>
            </div>
          </div>
        </header>

        {/* Redaction notice */}
        {detail.canvasserNamesRedacted && !detail.loading && (
          <div className="mx-5 mt-3 bg-bg-elevated/60 border border-border-base rounded p-2 text-[11px] text-fg-secondary">
            Canvasser names are hidden at your access level. Only
            coordinators and admins see individual attribution.
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {detail.loading ? (
            <div className="text-sm text-fg-muted">Loading knock list…</div>
          ) : detail.error ? (
            <div className="bg-status-danger-bg/30 border border-status-danger/40 rounded p-3 text-sm text-status-danger">
              {detail.error}
              <button
                type="button"
                onClick={detail.refetch}
                className="ml-3 inline-flex items-center min-h-[32px] px-2 py-1 rounded bg-status-danger hover:opacity-90 text-white text-xs font-semibold"
              >
                Retry
              </button>
            </div>
          ) : detail.kAnonymityApplied ? (
            <div className="bg-status-warning-bg/20 border border-status-warning/40 rounded p-3 text-xs text-status-warning">
              Row list suppressed for privacy (too few knocks remaining in
              this cell to show individual knockbacks).
            </div>
          ) : detail.rows.length === 0 ? (
            <div className="text-sm text-fg-muted">
              No knockbacks in this cell.
            </div>
          ) : (
            <>
              <p
                className="text-[11px] uppercase font-semibold text-fg-muted tabular-nums"
                data-testid="heatmap-detail-summary"
              >
                Showing {detail.rows.length} of {detail.totalKnocks}
              </p>
              <ul className="space-y-2" role="list">
                {detail.rows.map((row) => (
                  <li
                    key={row.knockback_id}
                    className="bg-bg-elevated/60 border border-border-base rounded-lg p-3 space-y-2"
                    data-testid="heatmap-detail-row"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${outcomeBadgeClass(
                          row.outcome
                        )}`}
                      >
                        {row.outcome.replace(/_/g, ' ').toLowerCase()}
                      </span>
                      <span className="text-[11px] text-fg-muted tabular-nums">
                        {relativeTime(row.timestamp)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-fg-secondary">
                        {row.voter_hint}
                      </span>
                      {row.canvasser_name ? (
                        <span className="text-[11px] text-fg-muted">
                          by {row.canvasser_name}
                        </span>
                      ) : (
                        <span className="text-[10px] italic text-fg-muted">
                          canvasser hidden
                        </span>
                      )}
                    </div>

                    {(row.candidate_lean || row.vote_intent) && (
                      <div className="flex gap-2 flex-wrap">
                        {row.candidate_lean && (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase ${leanBadgeClass(
                              row.candidate_lean
                            )}`}
                          >
                            {row.candidate_lean.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        )}
                        {row.vote_intent && (
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase bg-bg-raised text-fg-secondary">
                            {row.vote_intent.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        )}
                      </div>
                    )}

                    {row.top_issues && row.top_issues.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {row.top_issues.slice(0, 3).map((issue) => (
                          <span
                            key={issue}
                            className="inline-block px-2 py-0.5 rounded text-[10px] bg-bg-base/80 border border-border-base text-fg-secondary"
                          >
                            {issue.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}

                    {row.notes_snippet && (
                      <p className="text-[11px] text-fg-muted italic">
                        "{row.notes_snippet}"
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              {detail.hasMore && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={detail.loadMore}
                    disabled={detail.loadingMore}
                    className="w-full min-h-[44px] px-4 py-2 rounded bg-bg-elevated hover:bg-bg-elevated disabled:opacity-60 text-fg-secondary text-sm font-semibold border border-border-base focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {detail.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
